/**
 * Timeline Store
 * Manages timeline/Gantt chart state using Zustand
 */

import { create } from 'zustand';
import {
  TimelineData,
  TimelineTask,
  CriticalPathResult,
  TimelineFilters,
} from '@/types';

interface TimelineState {
  // State
  timelineData: TimelineData | null;
  criticalPath: CriticalPathResult | null;
  unscheduledTasks: TimelineTask[];
  overdueTasks: TimelineTask[];
  filters: TimelineFilters;
  isLoading: boolean;
  error: string | null;
  selectedTaskId: string | null;
  zoomLevel: 'day' | 'week' | 'month' | 'quarter';

  // Actions
  setTimelineData: (data: TimelineData | null) => void;
  setCriticalPath: (result: CriticalPathResult | null) => void;
  setUnscheduledTasks: (tasks: TimelineTask[]) => void;
  setOverdueTasks: (tasks: TimelineTask[]) => void;
  setFilters: (filters: TimelineFilters) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedTaskId: (taskId: string | null) => void;
  setZoomLevel: (level: 'day' | 'week' | 'month' | 'quarter') => void;
  updateTaskInTimeline: (task: TimelineTask) => void;
  toggleTaskExpansion: (taskId: string) => void;
  clearTimeline: () => void;
}

export const useTimelineStore = create<TimelineState>()((set) => ({
  // Initial state
  timelineData: null,
  criticalPath: null,
  unscheduledTasks: [],
  overdueTasks: [],
  filters: {
    showCompleted: false,
    hierarchyMode: 'flat',
  },
  isLoading: false,
  error: null,
  selectedTaskId: null,
  zoomLevel: 'week',

  // Actions
  setTimelineData: (data) => set({ timelineData: data }),

  setCriticalPath: (result) => set({ criticalPath: result }),

  setUnscheduledTasks: (tasks) => set({ unscheduledTasks: tasks }),

  setOverdueTasks: (tasks) => set({ overdueTasks: tasks }),

  setFilters: (filters) => set({ filters }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setSelectedTaskId: (taskId) => set({ selectedTaskId: taskId }),

  setZoomLevel: (level) => set({ zoomLevel: level }),

  updateTaskInTimeline: (task) =>
    set((state) => {
      if (!state.timelineData) return state;

      const updateTask = (tasks: TimelineTask[]): TimelineTask[] => {
        return tasks.map((t) => {
          if (t.taskId === task.taskId) {
            return task;
          }
          if (t.children) {
            return { ...t, children: updateTask(t.children) };
          }
          return t;
        });
      };

      return {
        timelineData: {
          ...state.timelineData,
          tasks: updateTask(state.timelineData.tasks),
        },
      };
    }),

  toggleTaskExpansion: (taskId) =>
    set((state) => {
      if (!state.timelineData) return state;

      const toggleExpansion = (tasks: TimelineTask[]): TimelineTask[] => {
        return tasks.map((t) => {
          if (t.taskId === taskId) {
            return { ...t, isExpanded: !t.isExpanded };
          }
          if (t.children) {
            return { ...t, children: toggleExpansion(t.children) };
          }
          return t;
        });
      };

      return {
        timelineData: {
          ...state.timelineData,
          tasks: toggleExpansion(state.timelineData.tasks),
        },
      };
    }),

  clearTimeline: () =>
    set({
      timelineData: null,
      criticalPath: null,
      unscheduledTasks: [],
      overdueTasks: [],
      selectedTaskId: null,
    }),
}));

// Selectors
export const useTimelineData = () =>
  useTimelineStore((state) => state.timelineData);

export const useTimelineTasks = () =>
  useTimelineStore((state) => state.timelineData?.tasks ?? []);

export const useCriticalPath = () =>
  useTimelineStore((state) => state.criticalPath);

export const useUnscheduledTasks = () =>
  useTimelineStore((state) => state.unscheduledTasks);

export const useOverdueTasks = () =>
  useTimelineStore((state) => state.overdueTasks);

export const useTimelineFilters = () =>
  useTimelineStore((state) => state.filters);

export const useTimelineLoading = () =>
  useTimelineStore((state) => state.isLoading);

export const useTimelineError = () =>
  useTimelineStore((state) => state.error);

export const useSelectedTimelineTask = () =>
  useTimelineStore((state) => state.selectedTaskId);

export const useTimelineZoomLevel = () =>
  useTimelineStore((state) => state.zoomLevel);

export const useCriticalTasks = () =>
  useTimelineStore((state) =>
    state.criticalPath?.criticalPath ?? []
  );

export default useTimelineStore;
